import { useState } from 'react';
import { requestLoginLink } from '../lib/auth';
import { LEGAL_TERMS_URL, LEGAL_PRIVACY_URL } from '../../shared/protocol';

/**
 * The magic-link sign-in surface: an email field → "sent" state. Used as the
 * slide-up sheet over create-flow screen 3 and as the standalone signed-out
 * gate for Friends/Settings. No password, ever — the link comes back to the
 * SPA with ?auth=<token_hash> (+ ?draft=1 when a create draft is waiting).
 */
export function AuthSheet({
  overlay,
  title,
  onClose,
}: {
  /** true = fixed sheet over the current screen (create flow); false = inline card */
  overlay?: boolean;
  title?: string;
  onClose?: () => void;
}) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      setError('Enter your email address.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      await requestLoginLink(e);
      setSentTo(e);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the sign-in link.');
    } finally {
      setSending(false);
    }
  };

  const inner = (
    <div className="card marquee auth-sheet">
      <h2 className="display">{title ?? 'One last thing'}</h2>
      {sentTo ? (
        <>
          <div className="notice success">
            Check <strong>{sentTo}</strong> — the link brings you right back.
          </div>
          <p className="soft">
            Nothing yet? Give it a minute and peek at spam. This tab keeps your draft safe.
          </p>
        </>
      ) : (
        <>
          <p className="soft">
            One-time sign-in so your friends and nights are saved. No password, ever.
          </p>
          <label htmlFor="auth-email">Your email</label>
          <input
            id="auth-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => (e.key === 'Enter' ? submit() : null)}
          />
          {error && <div className="notice error">{error}</div>}
          <button className="btn primary block" onClick={submit} disabled={sending}>
            {sending ? <span className="spinner" /> : 'Email me a sign-in link'}
          </button>
          {/* THU-115: the documents are named and linked BEFORE the email
              goes out, so the agreement recorded after sign-in is the second
              time someone has seen them rather than the first. This notice is
              not itself the assent — that is TermsGate, behind a real
              checkbox — it is the conspicuous notice that makes it fair. */}
          <p className="legal-inline">
            Signing in creates an account.{' '}
            <a href={LEGAL_TERMS_URL} target="_blank" rel="noreferrer noopener">
              Terms
            </a>{' '}
            ·{' '}
            <a href={LEGAL_PRIVACY_URL} target="_blank" rel="noreferrer noopener">
              Privacy
            </a>
          </p>
        </>
      )}
      {onClose && (
        <button className="btn link block" onClick={onClose}>
          Not now
        </button>
      )}
    </div>
  );

  if (!overlay) return inner;
  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true">
      <div className="sheet">{inner}</div>
    </div>
  );
}
